import { CheckboxInput, DropdownCheckbox, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

interface VehicleTypeOption {
  name: string;
  value: string;
  /** Catalog on/off toggle state — undefined while the status lookup resolves (no chip shown). */
  isActive?: boolean;
}

interface VehicleTypeMultiSelectProps {
  options: VehicleTypeOption[];
  value: string[];
  onChange: (next: string[]) => void;
  hasError?: boolean;
  disabled?: boolean;
}

const StatusChip = ({ isActive }: { isActive: boolean }) => (
  <span className="flex items-center gap-x-1.5">
    <span aria-hidden className={clsx("h-2 w-2 rounded-full", isActive ? "bg-success" : "bg-neutral-silver-gray")} />
    <Typography variant="small-regular" className="text-neutral-dark-gray">
      {isActive ? "Active" : "Inactive"}
    </Typography>
  </span>
);

/**
 * Multi-select for the vehicle's types (many-to-many). A vehicle can be attached
 * to multiple operator-defined types; the selection feeds own-fleet dispatch.
 * Types can be attached regardless of their catalog toggle — the Active/Inactive
 * chip shows whether the type is currently offered on new bookings.
 */
export const VehicleTypeMultiSelect = ({ options, value, onChange, hasError, disabled }: VehicleTypeMultiSelectProps) => {
  const selectedLabel = () => {
    const names = options.filter((o) => value.includes(o.value)).map((o) => o.name);
    return names.length > 0 ? names.join(", ") : "";
  };

  const toggle = (optionValue: string, checked: boolean) => {
    onChange(checked ? [...value, optionValue] : value.filter((v) => v !== optionValue));
  };

  return (
    <DropdownCheckbox
      className="w-full"
      placeholder="Select Vehicle Type"
      selectedCount={value.length}
      getSelectedLabel={selectedLabel}
      hasError={hasError}
      // The trigger sits low in SelectVehicleTypeModal — open upward so the
      // capped panel stays fully inside the modal instead of stretching the page.
      position="top"
    >
      {options.map((option) => (
        <div key={option.value} className="flex items-center justify-between gap-x-3">
          <CheckboxInput
            label={option.name}
            value={option.value}
            variant="check"
            checked={value.includes(option.value)}
            disabled={disabled}
            onChange={(e) => toggle(option.value, e.target.checked)}
          />
          {option.isActive !== undefined && <StatusChip isActive={option.isActive} />}
        </div>
      ))}
    </DropdownCheckbox>
  );
};
